import { useCallback, useEffect, useRef, useState } from "react";
import { getEvent, getTrashedEvents, type Event, type TrashedEvent } from "@/services/api/events";
import { hasPendingSaves, waitForPendingSaves } from "@/hooks/autosaveRegistry";

export interface OwnedEventState {
  /** Only ever the event the route asks for (uuid === requested id). */
  event: Event | null;
  /** Set when the requested event is in the owner's Trash (JUB-157). */
  trashedEvent: TrashedEvent | null;
  loading: boolean;
}

/**
 * Load the owner's event for the editor route.
 *
 * JUB-157 F11: a slow response for a previous route (or after unmount) must
 * never overwrite the current one. Every result is checked against a
 * monotonic request generation and the requested uuid; a 404 is followed by
 * a Trash lookup so a deleted event gets a "restore" state, not "not found".
 * The caller calls `reload()` whenever the route (or query) changes.
 */
export function useOwnedEvent(id: string | undefined, onUnavailable?: (uuid: string) => void) {
  const [state, setState] = useState<OwnedEventState>({ event: null, trashedEvent: null, loading: true });
  const generation = useRef(0);
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  const run = useCallback(async (silent: boolean) => {
    const current = ++generation.current;
    const isCurrent = () => current === generation.current;
    if (!id) {
      setState({ event: null, trashedEvent: null, loading: false });
      return;
    }

    // JUB-152: a silent refresh of the event already on screen keeps the
    // editor mounted (no spinner), so typing and focus survive it.
    setState((prev) => {
      const same = prev.event?.uuid === id;
      return { event: same ? prev.event : null, trashedEvent: null, loading: !(silent && same) };
    });

    try {
      // JUB-152: never read the event while an autosave for it is in flight.
      if (hasPendingSaves(id)) {
        await waitForPendingSaves(id);
        if (!isCurrent()) return;
      }
      const event = await getEvent(id);
      if (!isCurrent() || event.uuid !== id) return;
      setState({ event, trashedEvent: null, loading: false });
    } catch (error) {
      if (!isCurrent()) return;
      // JUB-152: a silent refresh that fails for any reason other than "not
      // found" (offline, 5xx) keeps the event on screen instead of turning
      // the editor into a not-found page mid-edit.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (silent && status !== 404) {
        setState((prev) => ({ ...prev, loading: false }));
        return;
      }
      const trashed = await getTrashedEvents()
        .then((items) => items.find((item) => item.uuid === id) ?? null)
        .catch(() => null);
      if (!isCurrent()) return;
      setState({ event: null, trashedEvent: trashed, loading: false });
      onUnavailableRef.current?.(id);
    }
  }, [id]);

  const load = useCallback(() => run(false), [run]);
  /** Re-read the current event without the loading state (JUB-152). */
  const refresh = useCallback(() => run(true), [run]);

  // The caller triggers loads (route and query changes); unmounting makes
  // anything still in flight stale.
  useEffect(() => () => {
    generation.current++;
  }, []);

  const setEvent = useCallback((event: Event) => {
    if (event.uuid !== id) return;
    // JUB-152: a saved response is newer than any read still in flight.
    generation.current++;
    setState({ event, trashedEvent: null, loading: false });
  }, [id]);

  return { ...state, reload: load, refresh, setEvent };
}
